import { useCallback, useEffect, useMemo, useRef } from "react";

type DebouncerFunction<T> = (data: T) => void;

// Use this hook to debounce a function call. The function will be called after the timeout has passed
// and no new signal has been sent. If a new signal is sent, the timeout is reset.
//
// Example:
//   const { signalStart, signalCancel } = useDebounce(() => {
//     console.log("Debounced function called");
//   }, 1000);
//
//   // Call signalStart() to start the debounce timer
//   // After 1000ms, the function will be called.
//   // If signalStart() is called again before the timeout has passed, the timer is reset
//   signalStart();
//
//   // Call signalCancel() to cancel the debounce timer, if present
//   signalCancel();
//
const useDebounce = <T = unknown>(callback: DebouncerFunction<T>, timeout: number) => {
  const ref = useRef<DebouncerFunction<T> | null>(null);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const signalCancel = useCallback(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }
  }, []);

  useEffect(() => signalCancel, [signalCancel]);

  const signalStart = useMemo(() => {
    const func = (data: T) => {
      ref.current?.(data);
    };

    return (data: T) => {
      signalCancel();
      debounceTimeout.current = setTimeout(() => {
        func(data);
      }, timeout);
    };
  }, [timeout, debounceTimeout, signalCancel]);

  const forceSignalStart = useMemo(() => {
    const func = (data: T) => {
      ref.current?.(data);
    };

    return (data: T) => {
      signalCancel();
      func(data);
    };
  }, [signalCancel]);

  return {
    signalStart,
    signalCancel,
    forceSignalStart,
  };
};

export { useDebounce };
